// What the browser does when a turbo-frame's request does not come back with
// that frame. application.js installs these two handlers on the document;
// spec/frontend/dom/frame_fallback_dom_check.mjs runs them against the real
// Turbo.

// A frame that loads something the page can live without (the person-signals
// badges, KI-418 night 59) marks itself `data-frame-fallback` and carries its
// failure content in a `<template data-frame-fallback-content>`. Whatever
// happens to its request, the page stays where it is:
//
//   * an answer without the frame (a 5xx page, a router timeout, the login
//     page after a session expired) would otherwise either write "Content
//     missing" into the frame or, for a redirect, navigate the WHOLE page to
//     it (the rule below). A badge must not be able to do either;
//   * a request that never gets an answer at all (the network dropped) would
//     otherwise leave the frame as it was, with every placeholder waiting.
//
// Both play the template instead. For the badges it holds turbo-streams that
// put a dash in every count, so the reader sees "not available" rather than a
// hole or a count that never arrives.
//
// Returns true when the frame is one of these, whether or not it had a
// template to play.
export function playFrameFallback(frame) {
  if (!(frame instanceof Element) || !frame.hasAttribute("data-frame-fallback")) return false

  const template = frame.querySelector("template[data-frame-fallback-content]")
  if (template) frame.replaceChildren(template.content.cloneNode(true))
  return true
}

// A redirect issued to a request that Turbo scoped to a frame must land as a
// full-page navigation when its destination has no matching frame. Turbo
// follows the frame request's redirect and looks for the frame in the
// destination; when it is absent (the login page after a session expiry being
// the canonical case) Turbo's default renders "Content missing" into the
// frame instead (known-issues #58, an app-wide class). Breaking out here
// covers every frame on every surface at once. visit(response) renders the
// already-fetched body, so the flash set by the redirecting controller
// survives. Non-redirect frame-missing responses keep Turbo's default, since
// they indicate a genuine frame-id mismatch worth surfacing in development.
//
// A frame with its own fallback is the exception, and it is checked first:
// breaking out of the page because a badge's request was redirected is the
// thing it exists to prevent.
export function onFrameMissing(event) {
  if (playFrameFallback(event.target)) {
    event.preventDefault()
    return
  }

  const { response, visit } = event.detail
  if (response && response.redirected) {
    event.preventDefault()
    visit(response)
  }
}

export function onFrameFetchRequestError(event) {
  playFrameFallback(event.target)
}
